import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  UserPlus,
  Edit3,
  ArrowLeft,
  Mail,
  User,
  Phone,
  AlertTriangle,
  Sparkles,
  Send,
  Save,
} from "lucide-react";
import Header from "../../Components/Header";
import Footer from "../../Components/Footer";
import FormControl from "../../../components/common/FormControl";
import ApiClient from "../../../methods/api/apiClient";
import loader from "../../../methods/loader";
import { toast } from "react-toastify";
import { DEFAULT_ROLE } from "./shared";

export default function TeamAddEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  // Form State - roleName is internal only, sent in payload, never shown on UI
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    roleName: DEFAULT_ROLE,
    mobileNo: "",
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [quotaExceeded, setQuotaExceeded] = useState(false);

  // Load existing customer in Edit Mode
  useEffect(() => {
    if (!isEdit) return;

    const fetchCustomerDetail = async () => {
      loader(true);
      try {
        const res = await ApiClient.get("tenants/portal/team/detail", { id }, "", true);
        if (res?.success || res?.data || res?.member) {
          const m = res.data?.member || res.data || res.member;
          setFormData({
            firstName: m.firstName || "",
            lastName: m.lastName || "",
            email: m.email || "",
            roleName: DEFAULT_ROLE,
            mobileNo: m.mobileNo || m.phone || "",
          });
        } else {
          toast.error("Failed to load customer profile");
          navigate("/tenant/customers");
        }
      } catch (err) {
        toast.error("Error loading customer profile");
      } finally {
        loader(false);
      }
    };

    fetchCustomerDetail();
  }, [id, isEdit, navigate]);

  // Validation
  const validate = () => {
    const errs = {};
    if (!formData.firstName.trim()) errs.firstName = "First name is required";
    if (!formData.lastName.trim()) errs.lastName = "Last name is required";

    if (!isEdit) {
      if (!formData.email.trim()) {
        errs.email = "Email address is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
        errs.email = "Please enter a valid email address";
      }
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Submit Handler: Always sends roleName: "User" in payload only
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    loader(true);
    setQuotaExceeded(false);

    try {
      if (isEdit) {
        // UPDATE MODE: POST tenants/portal/team/update
        const payload = {
          id,
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          roleName: DEFAULT_ROLE, // sent in payload only
          mobileNo: formData.mobileNo ? formData.mobileNo.trim() : "",
        };

        const res = await ApiClient.post("tenants/portal/team/update", payload, "", true);

        if (res?.success || !res?.error) {
          toast.success("Customer profile updated successfully");
          navigate(`/tenant/customers/detail/${id}`);
        } else {
          toast.error(res?.message || res?.error?.message || "Failed to update customer");
        }
      } else {
        // INVITE MODE: POST tenants/portal/team/invite
        const payload = {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim().toLowerCase(),
          roleName: DEFAULT_ROLE, // sent in payload only
          mobileNo: formData.mobileNo ? formData.mobileNo.trim() : "",
        };

        const res = await ApiClient.post("tenants/portal/team/invite", payload, "", true);

        if (res?.success || !res?.error) {
          toast.success(`Invitation email sent to ${payload.email}`);
          navigate("/tenant/customers");
        } else {
          const errMsg = res?.error?.message || res?.message || "";
          if (
            res?.status === 403 ||
            res?.error?.code === 403 ||
            errMsg.toLowerCase().includes("quota") ||
            errMsg.toLowerCase().includes("limit")
          ) {
            setQuotaExceeded(true);
            toast.error("Customer seat limit reached on current subscription plan");
          } else {
            toast.error(errMsg || "Failed to invite customer");
          }
        }
      }
    } catch (err) {
      if (err?.response?.status === 403 || err?.message?.includes("403")) {
        setQuotaExceeded(true);
        toast.error("Subscription customer quota reached.");
      } else {
        toast.error("An unexpected error occurred.");
      }
    } finally {
      loader(false);
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F4F7FB] antialiased text-slate-800">
      <Header />

      {/* Standard container matching Dashboard: max-w-5xl with mt-[60px] lg:mt-[70px] */}
      <div className="min-h-[calc(100vh_-_60px)] md:min-h-[calc(100vh_-_70px)] mt-[60px] lg:mt-[70px] py-6 lg:py-8">
        <div className="container mx-auto px-3 sm:px-6 lg:px-8 max-w-5xl space-y-6 pb-32">
          {/* Back Navigation Link */}
          <Link
            to="/tenant/customers"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[#115B9D] transition-colors"
          >
            <ArrowLeft size={14} />
            <span>Back to Customers</span>
          </Link>

          {/* Clean Modern Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight flex items-center gap-2.5">
                {isEdit ? (
                  <Edit3 size={24} className="text-[#115B9D]" />
                ) : (
                  <UserPlus size={24} className="text-[#115B9D]" />
                )}
                <span>{isEdit ? "Edit Customer" : "Invite Customer"}</span>
              </h1>
              <p className="text-xs sm:text-sm text-slate-500 mt-1">
                {isEdit
                  ? "Update customer credentials and contact mobile number."
                  : "Send an invitation email to a customer to access your quoting portal."}
              </p>
            </div>

            <Link
              to="/tenant/plans"
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-white border border-slate-200 text-xs font-semibold text-slate-600 hover:text-[#115B9D] hover:border-blue-200 transition-colors w-fit shadow-2xs"
            >
              <Sparkles size={13} className="text-amber-500" />
              <span>Subscription Quota</span>
            </Link>
          </div>

          {/* Quota Exceeded Alert Banner */}
          {quotaExceeded && (
            <div className="bg-amber-50 border border-amber-300 rounded-2xl p-4 sm:p-5 animate-in fade-in-50">
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-amber-700 shrink-0 mt-0.5" size={18} />
                <div>
                  <h4 className="text-xs sm:text-sm font-bold text-amber-900">
                    Customer Seat Quota Reached
                  </h4>
                  <p className="text-xs text-amber-800 mt-0.5 leading-relaxed">
                    Your organization subscription has reached its maximum customer accounts limit. Upgrade your subscription plan to invite more customers.
                  </p>
                  <Link
                    to="/tenant/plans"
                    className="inline-flex items-center gap-1.5 mt-2.5 px-3.5 py-1.5 bg-[#115B9D] text-white text-xs font-bold rounded-xl shadow-xs hover:bg-[#0e4b82] transition-colors"
                  >
                    <Sparkles size={13} />
                    <span>Upgrade Subscription Plan</span>
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* ─── FORM CARD (NO ROLE UI - PAYLOAD ONLY) ─── */}
          <div className="w-full bg-white rounded-3xl border border-slate-300 shadow-sm p-6 sm:p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* SECTION 1: Personal Details */}
              <div>
                <h3 className="text-xs font-black uppercase tracking-wider text-slate-500 mb-4 flex items-center gap-2">
                  <User size={14} className="text-[#115B9D]" />
                  <span>Customer Details</span>
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormControl
                    name="firstName"
                    label="First Name"
                    required={true}
                    placeholder="e.g. Sarah"
                    value={formData.firstName}
                    onChange={(val) => setFormData((prev) => ({ ...prev, firstName: val }))}
                    error={errors.firstName}
                  />

                  <FormControl
                    name="lastName"
                    label="Last Name"
                    required={true}
                    placeholder="e.g. Chef"
                    value={formData.lastName}
                    onChange={(val) => setFormData((prev) => ({ ...prev, lastName: val }))}
                    error={errors.lastName}
                  />
                </div>
              </div>

              {/* SECTION 2: Contact Information */}
              <div className="pt-4 border-t border-slate-200">
                <h3 className="text-xs font-black uppercase tracking-wider text-slate-500 mb-4 flex items-center gap-2">
                  <Mail size={14} className="text-[#115B9D]" />
                  <span>Contact Information</span>
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormControl
                    type="email"
                    name="email"
                    label="Email Address"
                    required={!isEdit}
                    disabled={isEdit}
                    placeholder="customer@company.com"
                    value={formData.email}
                    onChange={(val) => setFormData((prev) => ({ ...prev, email: val }))}
                    error={errors.email}
                  />

                  <FormControl
                    type="phone"
                    name="mobileNo"
                    label="Contact Mobile"
                    placeholder="+1 (555) 123-4567"
                    value={formData.mobileNo}
                    onChange={(val) => setFormData((prev) => ({ ...prev, mobileNo: val }))}
                    error={errors.mobileNo}
                  />
                </div>
              </div>

              {/* Form Actions Bar */}
              <div className="flex items-center justify-end gap-3 pt-6 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => navigate("/tenant/customers")}
                  className="px-5 py-2.5 rounded-xl border border-slate-300 text-slate-700 hover:bg-slate-50 text-xs sm:text-sm font-bold transition-colors cursor-pointer"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-gradient-to-r from-[#115B9D] to-[#1E88E5] text-white text-xs sm:text-sm font-bold shadow-md shadow-[#115B9D]/20 hover:shadow-lg transition-all cursor-pointer disabled:opacity-50"
                >
                  {isEdit ? <Save size={15} /> : <Send size={15} />}
                  <span>
                    {submitting
                      ? "Processing..."
                      : isEdit
                      ? "Save Changes"
                      : "Send Customer Invitation"}
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
